import { Divider, Drawer, styled } from '@mui/material';
import React, { startTransition, useCallback, useState } from 'react';
import { FullScreenHandle } from 'react-full-screen';
import PopUpMenu from '../../../../components/utils/PopUpMenu';
import { EXPORT_REF_ID } from '../../../constants';
import {
  useFilterSectionsContext,
  useGlobalFilterContext,
  useGlobalSegmentationLevel1Context,
  useGlobalTimeSliderContext,
  useSegmentationSectionsContext,
} from '../../../context/contexts';
import { PageHandle } from '../dashboards/types';
import ExportDialog from '../export/Dialog';
import { itemStateToString } from '../filter/utils';
import { timeSliderStateToCacheKey } from '../timeslider/utils';
import { ExportModalProvider } from './contexts';
import { DisplaySettingsMenu } from './DisplaySettingsMenu';
import { ExportMenu } from './ExportMenu';
import { ExportOptionsItem } from './ExportOptionsItem';
import { FilterFilterDrawer } from './FilterFilterDrawer';
import { useShowExportOptions } from './hooks';
import { PageNumbers } from './PageNumbers';
import { SegmentationFilterDrawer } from './SegmentationFilterDrawer';
import { SideBarBottom } from './SideBarBottom';
import { SideBarMiddle } from './SideBarMiddle';
import { SideBarTop } from './SideBarTop';
import { FilterState, SegmentationState } from './types';

const StyledDrawer = styled(Drawer)<{ $isExpanded: boolean }>`
  width: ${(props) => (props.$isExpanded ? '15vw' : '3em')};
  z-index: 10;
  transition: ease 0.3s 0s;
  overflow: ${(props) => (props.$isExpanded ? 'auto' : 'hidden')};
  will-change: width;
`;

const StyledPopupMenu = styled(PopUpMenu)`
  &:hover {
    transform: translateX(-12vw);
    background-color: green;
  }
` as typeof PopUpMenu;

interface SideBarProps {
  pageHandle: PageHandle;
  fullScreenHandle: FullScreenHandle;
  backgroundColor?: string;
}

export const SideBar = (props: SideBarProps) => {
  const { pageHandle, fullScreenHandle, backgroundColor } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuOpen, setMenuOpen] = useState('');
  const [filterDrawerState, setFilterDrawerState] =
    useState<FilterState>('closed');
  const [segmentationDrawerState, setSegmentationDrawerState] =
    useState<SegmentationState>('closed');
  const [isExpanded, setIsExpanded] = useState(false);
  const timeSliderHandle = useGlobalTimeSliderContext();
  const [timeSliderState] = timeSliderHandle;
  const filterHandle = useGlobalFilterContext();
  const [filterState] = filterHandle;
  const segmentationHandle = useGlobalSegmentationLevel1Context();
  const [segmentationState] = segmentationHandle;

  const filterSections = useFilterSectionsContext();
  const segmentationSections = useSegmentationSectionsContext();

  const showExportOptions = useShowExportOptions(pageHandle.selected);
  const exportCacheKey = `${pageHandle.selected.dashboardId}-${pageHandle.selected.pageId}-${timeSliderStateToCacheKey(
    timeSliderState
  )}-${itemStateToString(filterState)}-${itemStateToString(segmentationState)}`;

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, menuName: string) => {
      if (menuOpen === menuName) {
        handleMenuClose();
      } else {
        setAnchorEl(event.currentTarget);
        setMenuOpen(menuName);
      }
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setMenuOpen('');
  }, []);

  const handleFilterDrawerState = (fs: FilterState) => {
    startTransition(() => {
      setFilterDrawerState(fs);
    });
  };

  const handleSegmentationDrawerState = (fs: SegmentationState) => {
    startTransition(() => {
      setSegmentationDrawerState(fs);
    });
  };

  return (
    <>
      <FilterFilterDrawer
        isFullScreen={fullScreenHandle.active}
        drawerState={filterDrawerState}
        setDrawerState={handleFilterDrawerState}
        filterSections={filterSections}
        filterHandle={filterHandle}
      />
      <SegmentationFilterDrawer
        isFullScreen={fullScreenHandle.active}
        drawerState={segmentationDrawerState}
        setDrawerState={handleSegmentationDrawerState}
        filterSections={segmentationSections}
        segmentationHandle={segmentationHandle}
      />

      <StyledDrawer
        open={true}
        variant="permanent"
        onMouseEnter={() => {
          setIsExpanded(true);
        }}
        onMouseLeave={() => {
          setIsExpanded(false);
        }}
        PaperProps={{
          style: {
            width: 'inherit',
            zIndex: 1099,
            overflow: 'hidden',
            backgroundColor,
          },
        }}
        $isExpanded={!!(isExpanded || menuOpen)}
      >
        <div
          data-cy="panalyt-dashboard-sidebar"
          style={{
            width: '100%',
            height: '100%',
            overflow: 'hidden auto',
          }}
        >
          <Divider />
          <SideBarTop
            setMenuOpen={handleMenuOpen}
            setFilterDrawerState={handleFilterDrawerState}
            setSegmentationLevel1DrawerState={handleSegmentationDrawerState}
          />
          <Divider />
          <ExportOptionsItem setMenuOpen={handleMenuOpen} />
          <Divider />
          <PageNumbers pageHandle={pageHandle} />
          <Divider />
          <SideBarMiddle />
          <Divider />
          <SideBarBottom setMenuOpen={handleMenuOpen} />
        </div>
      </StyledDrawer>
      <ExportModalProvider>
        <StyledPopupMenu
          open={menuOpen !== ''}
          anchorEl={anchorEl}
          onClose={() => handleMenuClose()}
          backgroundColor="whitesmoke"
          closeOnClickAway={false}
          onMouseLeave={() => handleMenuClose()}
          maxHeight="50vh"
        >
          {menuOpen === 'displaySettings' && <DisplaySettingsMenu />}
          <ExportMenu
            menuOpen={menuOpen === 'export'}
            showExportOptions={showExportOptions}
          />
        </StyledPopupMenu>
        <ExportDialog
          isFullScreen={fullScreenHandle.active}
          exportedElementId={EXPORT_REF_ID}
          cacheKey={exportCacheKey}
        />
      </ExportModalProvider>
    </>
  );
};
